import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateAuditStatusConfig } from "@/hooks/use-audit-status-configs";
import { cn } from "@/lib/utils";
import { STATUS_COLORS } from "./status-colors";

const createSchema = z.object({
  label: z.string().min(1, "Label is required").max(50, "Label must be 50 characters or fewer"),
  key: z
    .string()
    .min(1, "Key is required")
    .max(50, "Key must be 50 characters or fewer")
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Key must start with a letter and contain only lowercase letters, numbers, and underscores",
    ),
  color: z.string().min(1, "Color is required"),
});

type CreateValues = z.infer<typeof createSchema>;

interface CreateAuditStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAuditStatusDialog({ open, onOpenChange }: CreateAuditStatusDialogProps) {
  const create = useCreateAuditStatusConfig();

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { label: "", key: "", color: STATUS_COLORS[0]?.value ?? "#6b7280" },
  });

  function onSubmit(values: CreateValues) {
    create.mutate(values, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  function handleLabelChange(value: string) {
    form.setValue("label", value);
    const autoKey = value
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/^[^a-z]+/, "");
    form.setValue("key", autoKey, { shouldValidate: form.formState.touchedFields.key });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Status</DialogTitle>
          <DialogDescription>Create a custom audit status.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Under Legal Review"
                      {...field}
                      onChange={(e) => handleLabelChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Key</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. under_legal_review" {...field} />
                  </FormControl>
                  <FormDescription>
                    Unique identifier used in the system. Auto-filled from the label.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_COLORS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          title={c.label}
                          onClick={() => field.onChange(c.value)}
                          className={cn(
                            "h-7 w-7 rounded-full ring-offset-background transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                            field.value === c.value
                              ? "ring-2 ring-foreground ring-offset-2 scale-110"
                              : "hover:scale-105",
                          )}
                          style={{ backgroundColor: c.value }}
                        />
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating..." : "Create Status"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
